import * as styles from "./TimeRangePicker.css";

import { clsx } from "clsx";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { JSX } from "react";

import { TimeRangePickerContext } from "./TimeRangePicker.context";
import type {
  MinuteRange,
  TimeRange,
  TimeRangePickerContextValue,
  TimeRangePickerProps,
} from "./TimeRangePicker.types";
import {
  DEFAULT_MAJOR_TICK,
  DEFAULT_MAX,
  DEFAULT_MIN,
  DEFAULT_MINOR_TICK,
  DEFAULT_STEP,
  MAJOR_TICK_TARGET,
  MINOR_TICK_TARGET,
  MIN_VIEW,
} from "./constants";
import {
  assignLanes,
  clamp,
  clampView,
  fromMinuteRange,
  neighbourBounds,
  snap,
  tickStepFor,
  toMinuteRange,
  toMinutes,
} from "./timeMath";

const EMPTY: TimeRange[] = [];

export function TimeRangePicker({
  value,
  defaultValue = EMPTY,
  onValueChange,
  onValueCommit,
  selectedIndex,
  defaultSelectedIndex = null,
  onSelectedIndexChange,
  onRemove,
  view,
  defaultView,
  onViewChange,
  selectionBoundary,
  min: minTime = DEFAULT_MIN,
  max: maxTime = DEFAULT_MAX,
  step = DEFAULT_STEP,
  minDuration = step,
  majorTick = DEFAULT_MAJOR_TICK,
  minorTick = DEFAULT_MINOR_TICK,
  overlap = "forbid",
  disabled = false,
  readOnly = false,
  className,
  children,
}: TimeRangePickerProps): JSX.Element {
  const rootRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);

  const isControlled = value !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue);
  const currentValue = isControlled ? value : internalValue;

  const latestValue = useRef(currentValue);

  useLayoutEffect(() => {
    latestValue.current = currentValue;
  }, [currentValue]);

  const isSelectionControlled = selectedIndex !== undefined;
  const [internalSelected, setInternalSelected] =
    useState(defaultSelectedIndex);
  const currentSelected = isSelectionControlled
    ? selectedIndex
    : internalSelected;

  const min = toMinutes(minTime) ?? 0;
  const max = Math.max(min + step, toMinutes(maxTime) ?? 24 * 60);

  const minView = Math.max(step * 4, MIN_VIEW);

  // `null` stands for the whole day rather than a window of its own width, so
  // bounds that widen later, around a range drawn past them, keep being shown
  // whole. It becomes a window of its own the moment one is asked for.
  const [internalView, setInternalView] = useState<MinuteRange | null>(() =>
    defaultView
      ? {
          begin: toMinutes(defaultView.begin) ?? min,
          end: toMinutes(defaultView.end) ?? max,
        }
      : null,
  );

  const isViewControlled = view !== undefined;

  const currentView = useMemo(
    () =>
      clampView(
        isViewControlled
          ? {
              begin: toMinutes(view.begin) ?? min,
              end: toMinutes(view.end) ?? max,
            }
          : (internalView ?? { begin: min, end: max }),
        min,
        max,
        minView,
      ),
    [isViewControlled, view, internalView, min, max, minView],
  );

  const setView = useCallback(
    (next: MinuteRange) => {
      const clamped = clampView(next, min, max, minView);

      if (!isViewControlled) {
        setInternalView(
          clamped.begin === min && clamped.end === max ? null : clamped,
        );
      }

      onViewChange?.(fromMinuteRange(clamped));
    },
    [isViewControlled, min, max, minView, onViewChange],
  );

  // The grid refines itself as the window narrows, so a zoomed-in track never
  // shows a single line two hours apart.
  const span = currentView.end - currentView.begin;
  const majorStep = tickStepFor(span, majorTick, MAJOR_TICK_TARGET);
  const minorStep = tickStepFor(span, minorTick, MINOR_TICK_TARGET);

  const ranges = useMemo(() => currentValue.map(toMinuteRange), [currentValue]);
  const lanes = useMemo(
    () =>
      overlap === "stack"
        ? assignLanes(ranges)
        : ranges.map(() => ({ lane: 0, lanes: 1 })),
    [ranges, overlap],
  );

  const emit = useCallback(
    (next: TimeRange[]) => {
      latestValue.current = next;

      if (!isControlled) {
        setInternalValue(next);
      }

      onValueChange?.(next);
    },
    [isControlled, onValueChange],
  );

  const select = useCallback(
    (index: number | null) => {
      if (!isSelectionControlled) {
        setInternalSelected(index);
      }

      onSelectedIndexChange?.(index);
    },
    [isSelectionControlled, onSelectedIndexChange],
  );

  useEffect(() => {
    if (null === currentSelected) {
      return;
    }

    const handlePointerDown = (event: globalThis.PointerEvent) => {
      const path = event.composedPath();

      const inside = [rootRef.current, selectionBoundary?.current].some(
        (element) => element && path.includes(element),
      );

      const floating = path.some(
        (node) =>
          node instanceof Element && node.hasAttribute("data-base-ui-portal"),
      );

      if (!inside && !floating) {
        select(null);
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
    };
  }, [currentSelected, select, selectionBoundary]);

  const updateRange = useCallback(
    (index: number, range: MinuteRange) => {
      const current = latestValue.current;
      const next = fromMinuteRange(range);
      const previous = current[index];

      if (
        previous &&
        previous.begin === next.begin &&
        previous.end === next.end
      ) {
        return;
      }

      emit(current.map((item, at) => (at === index ? next : item)));
    },
    [emit],
  );

  const addRange = useCallback(
    (range: MinuteRange) => {
      const current = latestValue.current;

      emit([...current, fromMinuteRange(range)]);

      return current.length;
    },
    [emit],
  );

  const removeRange = useCallback(
    (index: number) => {
      if (false === onRemove?.(index)) {
        return;
      }

      emit(latestValue.current.filter((_, at) => at !== index));
      select(null);
    },
    [emit, select, onRemove],
  );

  const commit = useCallback(() => {
    onValueCommit?.(latestValue.current);
  }, [onValueCommit]);

  const boundsOf = useCallback<TimeRangePickerContextValue["boundsOf"]>(
    (index, around) => {
      const range = null === index ? null : (ranges[index] ?? null);
      const anchor =
        range ?? (around === undefined ? null : { begin: around, end: around });

      if (overlap === "stack" || null === anchor) {
        return { begin: min, end: max };
      }

      return neighbourBounds(ranges, index, anchor, min, max);
    },
    [ranges, overlap, min, max],
  );

  const minutesAt = useCallback(
    (clientX: number) => {
      const rect = trackRef.current?.getBoundingClientRect();

      if (!rect || 0 === rect.width) {
        return currentView.begin;
      }

      const ratio = (clientX - rect.left) / rect.width;

      return clamp(
        snap(currentView.begin + ratio * span, step),
        currentView.begin,
        currentView.end,
      );
    },
    [currentView, span, step],
  );

  // The time under the pointer stays under the pointer, the way a map keeps
  // the place you zoom on.
  const zoomAt = useCallback(
    (anchor: number, factor: number) => {
      const next = clamp(span * factor, minView, max - min);
      const ratio = 0 === span ? 0.5 : (anchor - currentView.begin) / span;
      const begin = clamp(anchor - ratio * next, min, max - next);

      setView({ begin, end: begin + next });
    },
    [span, currentView, min, max, minView, setView],
  );

  const panBy = useCallback(
    (minutes: number) => {
      const begin = clamp(currentView.begin + minutes, min, max - span);

      setView({ begin, end: begin + span });
    },
    [currentView, span, min, max, setView],
  );

  const percentOf = useCallback(
    (minutes: number) => ((minutes - currentView.begin) / span) * 100,
    [currentView, span],
  );

  const context = useMemo<TimeRangePickerContextValue>(
    () => ({
      ranges,
      lanes,
      min,
      max,
      view: currentView,
      minView,
      step,
      minDuration: Math.max(step, minDuration),
      majorStep,
      minorStep,
      overlap,
      disabled,
      readOnly,
      selectedIndex: currentSelected,
      trackRef,
      select,
      setView,
      zoomAt,
      panBy,
      updateRange,
      addRange,
      removeRange,
      commit,
      boundsOf,
      minutesAt,
      percentOf,
    }),
    [
      ranges,
      lanes,
      min,
      max,
      currentView,
      minView,
      step,
      minDuration,
      majorStep,
      minorStep,
      overlap,
      disabled,
      readOnly,
      currentSelected,
      select,
      setView,
      zoomAt,
      panBy,
      updateRange,
      addRange,
      removeRange,
      commit,
      boundsOf,
      minutesAt,
      percentOf,
    ],
  );

  return (
    <TimeRangePickerContext.Provider value={context}>
      <div
        ref={rootRef}
        data-slot="time-range-picker"
        data-disabled={disabled || undefined}
        data-readonly={readOnly || undefined}
        className={clsx(styles.root, className)}
      >
        {children}
      </div>
    </TimeRangePickerContext.Provider>
  );
}
